import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  patchUsersUserSettingsQuickPayment,
  type UpdateQuickPaymentEnabledRequest,
  type UserInfoQueryResult,
} from "Infrastructure/Api/Api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

type Props = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

export const useUpdateQuickPaymentMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation<
    void,
    Error,
    UpdateQuickPaymentEnabledRequest,
    { previousUserInfo: UserInfoQueryResult | undefined }
  >({
    mutationFn: processResponseWithApiProblemDetails(
      (data: UpdateQuickPaymentEnabledRequest) =>
        patchUsersUserSettingsQuickPayment(data),
    ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: userInfoKeys.all });

      const previousUserInfo = queryClient.getQueryData<UserInfoQueryResult>(
        userInfoKeys.all,
      );

      queryClient.setQueryData<UserInfoQueryResult>(userInfoKeys.all, (old) =>
        old ? { ...old, isQuickPaymentEnabled: variables.isEnabled } : old,
      );

      return { previousUserInfo };
    },
    onError: (error, _variables, context) => {
      if (context?.previousUserInfo) {
        queryClient.setQueryData(userInfoKeys.all, context.previousUserInfo);
      }
      toast.error(t(Resources.Common.SaveError));
      onError?.(error);
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userInfoKeys.all });
    },
  });
};
